import { gql, useMutation } from '@apollo/client';
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Code,
  Divider,
  FormControl,
  Heading,
  Switch,
  Text,
  useToast,
} from '@apollo/orbit';
import classnames from 'classnames';
import { useFormik } from 'formik';
import _ from 'lodash';
import React, { useCallback, useState } from 'react';
import * as Yup from 'yup';

import { useAriaDescribedBySetting } from 'src/hooks/useAriaDescribedBySetting';
import { useDebouncedEffect } from 'src/hooks/useDebouncedEffect';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

import { AUTOSAVE_TIMEOUT_MS } from '../../checksConfigPage/constants';
import { UseGraphSettingsButton } from '../../variantChecksConfigPage/common/UseGraphSettingsButton';
import { SharedLinterCheckConfigProps } from '../shared';

export const DefaultIgnoreLinterSettingsFragment = gql`
  fragment DefaultIgnoreLinterSettingsFragment on GraphLinterConfiguration {
    ignoreDeprecated
    ignoreInaccessible
  }
`;

const validationSchema = Yup.object().shape({
  enableLintChecks: Yup.boolean().required(),
  ignoreDeprecated: Yup.boolean().required(),
  ignoreInaccessible: Yup.boolean().required(),
});

type FormValues = Yup.InferType<typeof validationSchema>;

export const DefaultLinterSettingsCard = ({
  graphRef,
  isVariantConfig,
  checkConfig,
  linterConfig,
  graphTitle,
}: SharedLinterCheckConfigProps & { graphTitle: string }) => {
  const toast = useToast();
  const [toggleDisabled, setToggleDisabled] = useState(false);
  const [updateCheckConfig] = useMutation<
    GraphQLTypes.LinterCheckConfigDefaultSettingsMutation,
    GraphQLTypes.LinterCheckConfigDefaultSettingsMutationVariables
  >(
    gql`
      mutation LinterCheckConfigDefaultSettingsMutation(
        $graphId: ID!
        $enableLintChecks: Boolean
        $changes: GraphLinterConfigurationChangesInput!
      ) {
        service(id: $graphId) {
          id
          updateCheckConfiguration(enableLintChecks: $enableLintChecks) {
            id
            enableLintChecks
          }
          updateLinterConfiguration(changes: $changes) {
            ignoreDeprecated
            ignoreInaccessible
          }
        }
      }
    `,
    {
      onCompleted: () => {
        setToggleDisabled(false);
      },
    },
  );

  const { values, errors, dirty, setFieldValue, submitForm } =
    useFormik<FormValues>({
      enableReinitialize: true,
      initialValues: {
        // checkConfig will likely never be null here but b/c we're enabling this
        // for everybody by default in the BE, enableLintChecks is set to true in the form
        enableLintChecks: checkConfig ? checkConfig.enableLintChecks : true,
        ignoreDeprecated: linterConfig.ignoreDeprecated,
        ignoreInaccessible: linterConfig.ignoreInaccessible,
      },
      onSubmit: async (formValues) => {
        const changedEnableLintChecks =
          formValues.enableLintChecks !== checkConfig?.enableLintChecks;
        const changedIgnoreDeprecated =
          formValues.ignoreDeprecated !== linterConfig?.ignoreDeprecated;
        const changedIgnoreInaccessible =
          formValues.ignoreInaccessible !== linterConfig?.ignoreInaccessible;

        await updateCheckConfig({
          variables: {
            graphId: graphRef.graphId,
            enableLintChecks: changedEnableLintChecks
              ? formValues.enableLintChecks
              : checkConfig?.enableLintChecks,
            changes: {
              ignoreDeprecated: changedIgnoreDeprecated
                ? formValues.ignoreDeprecated
                : null,
              ignoreInaccessible: changedIgnoreInaccessible
                ? formValues.ignoreInaccessible
                : null,
            },
          },
          update: (cache) => {
            cache.evict({
              id: cache.identify({
                __typename: 'Service',
                id: graphRef.graphId,
              }),
              fieldName: 'checkConfiguration',
            });
          },
        });

        if (changedEnableLintChecks) {
          toast({
            status: 'info',
            duration: 3000,
            title: formValues.enableLintChecks
              ? 'Linter Enabled'
              : 'Linter Disabled',
            description: (
              <div>
                You've {formValues.enableLintChecks ? 'enabled' : 'disabled'}{' '}
                the linter for all variants on the{' '}
                <span className="font-semibold">{graphTitle}</span> graph.
              </div>
            ),
          });
        }
        if (changedIgnoreDeprecated) {
          toast({
            status: 'info',
            duration: 3000,
            title: formValues.ignoreDeprecated
              ? 'Ignore @deprecated enabled'
              : 'Ignore @deprecated disabled',
            description: (
              <div>
                {formValues.ignoreDeprecated
                  ? 'Ignoring'
                  : 'No longer ignoring'}{' '}
                rule violations on <Code>@deprecated</Code> definitions.
              </div>
            ),
          });
        }
        if (changedIgnoreInaccessible) {
          toast({
            status: 'info',
            duration: 3000,
            title: formValues.ignoreInaccessible
              ? 'Ignore @inaccessible enabled'
              : 'Ignore @inaccessible disabled',
            description: (
              <div>
                {formValues.ignoreInaccessible
                  ? 'Ignoring'
                  : 'No longer ignoring'}{' '}
                rule violations on <Code>@inaccessible</Code> definitions.
              </div>
            ),
          });
        }
      },
      validationSchema,
    });

  const submitIfDirty = useCallback(() => {
    if (dirty && _.isEmpty(errors)) {
      submitForm();
    }
  }, [dirty, errors, submitForm]);

  useDebouncedEffect(submitIfDirty, AUTOSAVE_TIMEOUT_MS, [values]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>General linter configuration</CardTitle>
        {isVariantConfig && (
          <FormControl>
            <UseGraphSettingsButton
              label="Use Graph Settings"
              tooltipContent="At this time, variant level overrides for the linter configuration are not available. Please make changes at the graph level."
              isSelected={true}
              // NOTE: We are only including graph level configuration at this
              // time so we should keep this disabled and leave the variant version
              // of this component in a read only state
              disabled={true}
              onChange={_.noop}
              trackingEventCategory="Lint Checks"
            />
          </FormControl>
        )}
      </CardHeader>
      <CardBody>
        <Setting
          isSelected={values.enableLintChecks}
          onChange={(newValue) => {
            setToggleDisabled(true);
            setFieldValue('enableLintChecks', newValue);
          }}
          isDisabled={isVariantConfig || toggleDisabled}
          label="Enable Linter"
          analyticsId="enable-lint-checks"
        >
          <Text>
            {isVariantConfig
              ? 'Run the linter on this variant.'
              : 'Run the linter on all graph variants.'}
          </Text>
        </Setting>
        <Divider />
        <Setting
          isSelected={values.ignoreDeprecated}
          onChange={(newValue) => {
            setToggleDisabled(true);
            setFieldValue('ignoreDeprecated', newValue);
          }}
          isDisabled={isVariantConfig || toggleDisabled}
          label="Ignore @deprecated"
          analyticsId="lint-ignore-deprecated"
        >
          <Text>
            Ignore rule violations on elements where{' '}
            <span>
              <Code>@deprecated</Code>
            </span>{' '}
            is applied, excluding{' '}
            <span>
              <Code>
                <a
                  href="#DEPRECATED_DIRECTIVE_MISSING_REASON"
                  className="text-link hover:underline"
                >
                  DEPRECATED_DIRECTIVE_MISSING_REASON
                </a>
              </Code>
            </span>
            .
          </Text>
        </Setting>
        <Divider />
        <Setting
          isSelected={values.ignoreInaccessible}
          onChange={(newValue) => {
            setToggleDisabled(true);
            setFieldValue('ignoreInaccessible', newValue);
          }}
          isDisabled={isVariantConfig || toggleDisabled}
          label="Ignore @inaccessible"
          analyticsId="lint-ignore-inaccessible"
        >
          <Text>
            Ignore rule violations on elements where{' '}
            <span>
              <Code>@inaccessible</Code>{' '}
            </span>
            is applied.
          </Text>
        </Setting>
      </CardBody>
    </Card>
  );
};

const Setting = ({
  analyticsId,
  isSelected,
  onChange,
  children,
  label,
  isDisabled,
}: {
  analyticsId: string;
  label: React.ReactNode;
  isSelected: React.ComponentProps<typeof Switch>['isChecked'];
  onChange: (isCheckced: boolean) => void;
  children?: React.ReactNode;
  isDisabled?: boolean;
}) => {
  const describedBySetting = useAriaDescribedBySetting(
    'linter-settings-description-',
  );

  return (
    <div>
      {children && (
        <div className="text-neutral" aria-hidden id={describedBySetting}>
          <Heading className="flex flex-row items-center gap-2" as="h3">
            {label}
            <span>
              <Switch
                data-analytics-label={`linter_settings ${analyticsId} ${
                  isSelected ? 'on' : 'off'
                }`}
                data-analytics-category="Lint Checks"
                isChecked={isSelected}
                onChange={(e) => {
                  onChange?.(e.target.checked);
                }}
                aria-describedby={describedBySetting}
                isDisabled={isDisabled}
                className={classnames(
                  'shrink-0 items-start',
                  isDisabled
                    ? 'opacity-100 [&>div:nth-child(3)]:opacity-50 [&>div:nth-child(4)]:opacity-50'
                    : 'cursor-pointer',
                )}
              />
            </span>
          </Heading>
          {children}
        </div>
      )}
    </div>
  );
};
